import { SubmitHandler } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@givebutter/primitives/toast';
import { DATE_MASK_FORMAT, maskDecimal, unmaskNumber } from '@givebutter/util';
import * as plansApi from 'api/transactionPlans';
import * as Dialog from 'components/dialog';
import * as Form from 'components/form';
import { InputCurrencyIcon } from 'components/input';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useForceRender } from 'customHooks/useForceRender';
import { isoDateFromServer } from 'helpers/dates';
import { DateTime } from 'luxon';
import { accountQueryKeys } from 'queries/account-query-keys';
import { TransactionPlan } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface EditRecurringPlansFormValues {
    nextBillDate: DateTime;
    amount: string;
}

interface EditPlanDialogProps extends Dialog.DialogProps {
    plan: TransactionPlan;
}

const EditPlanDialog = (props: EditPlanDialogProps) => {
    const { key, forceRender } = useForceRender();
    const { success, error } = useToast();
    const { plan, children, ...rest } = props;
    const queryClient = useQueryClient();
    const { accountId } = useCurrentAccount();

    const methods = Form.useForm<EditRecurringPlansFormValues>({
        defaultValues: {
            nextBillDate: isoDateFromServer(plan.next_bill_date),
            amount: maskDecimal(plan.amount),
        },
    });
    const { handleSubmit, control, path } = methods;

    const { mutate: editPlan, isPending: isLoading } = useMutation({
        mutationFn: (values: EditRecurringPlansFormValues) =>
            plansApi.editPlan({ accountId, planId: plan.id, data: toRequest(values) }),
        onSuccess() {
            success('Plan updated.');
            queryClient.invalidateQueries({ queryKey: accountQueryKeys.plans(accountId) });
            forceRender();
        },
        onError: (e: GivebutterAxiosError) =>
            error(e.response?.data?.message ?? 'Failed to update plan'),
    });

    return (
        <Form.Provider key={key} {...methods}>
            <Dialog.Root {...rest}>
                {children}
                <Dialog.Portal>
                    <Dialog.Overlay />
                    <Dialog.Content>
                        <form
                            onSubmit={handleSubmit(
                                editPlan as SubmitHandler<EditRecurringPlansFormValues>,
                            )}
                            className={'contents'}
                        >
                            <Dialog.Close />
                            <Dialog.Title>Edit Plan</Dialog.Title>
                            <div className={'mb-6 flex flex-col gap-y-6 py-6'}>
                                <Form.Container>
                                    <Form.Label
                                        name={path('nextBillDate')}
                                        className={'font-medium'}
                                    >
                                        Next Bill Date
                                    </Form.Label>
                                    <Form.UseFormDatePicker
                                        control={control}
                                        name={'nextBillDate'}
                                        rules={{
                                            validate: value =>
                                                DateTime.now() < value
                                                    ? true
                                                    : 'Date must be after today.',
                                        }}
                                    >
                                        <Form.Error name={path('nextBillDate')} />
                                    </Form.UseFormDatePicker>
                                </Form.Container>
                                <Form.Container>
                                    <Form.Label name={path('amount')} className={'font-medium'}>
                                        Amount
                                    </Form.Label>
                                    <Form.TextInputRoot
                                        control={control}
                                        name={'amount'}
                                        rules={{
                                            required: 'Must provide an amount.',
                                        }}
                                    >
                                        <InputCurrencyIcon />
                                        <Form.TextInputForm placeholder={'25.00'} />
                                    </Form.TextInputRoot>
                                    <Form.Error name={path('amount')} />
                                </Form.Container>
                            </div>
                            <div className={'flex justify-end'}>
                                <Form.SubmitButton isSubmitting={isLoading} />
                            </div>
                        </form>
                    </Dialog.Content>
                </Dialog.Portal>
            </Dialog.Root>
        </Form.Provider>
    );
};

const EditPlanDialogTrigger = Dialog.Trigger;

const Root = EditPlanDialog;
const Trigger = EditPlanDialogTrigger;

const toRequest = (form: EditRecurringPlansFormValues): plansApi.EditPlanRequest => ({
    next_bill_date: form.nextBillDate.toFormat(DATE_MASK_FORMAT),
    amount: unmaskNumber(form.amount),
});

export {
    EditPlanDialog,
    EditPlanDialogTrigger,
    //
    Root,
    Trigger,
};
